/**
 * Pure helpers for the learning parts of play-page state. Kept out of the
 * React reducer so they can be unit-tested.
 */
import type { LearningView, SessionPoints, VocabCardView, WritingRules } from "@storyai/shared";

export const ZERO_POINTS: SessionPoints = { reading: 0, vocabulary: 0, writing: 0 };

/** Adds cards to the pending queue without duplicating ones already there. */
export function mergePendingCards(pending: VocabCardView[], incoming: VocabCardView[]): VocabCardView[] {
  const seen = new Set(pending.map((c) => c.vocabWordId));
  const out = [...pending];
  for (const c of incoming) {
    if (seen.has(c.vocabWordId)) continue;
    seen.add(c.vocabWordId);
    out.push(c);
  }
  return out;
}

/** Per-kind deltas between two point totals; only positive changes. */
export function pointsDelta(before: SessionPoints, after: SessionPoints): { kind: keyof SessionPoints; amount: number }[] {
  const out: { kind: keyof SessionPoints; amount: number }[] = [];
  for (const kind of ["reading", "vocabulary", "writing"] as const) {
    const d = after[kind] - before[kind];
    if (d > 0) out.push({ kind, amount: d });
  }
  return out;
}

export function totalPoints(p: SessionPoints): number {
  return p.reading + p.vocabulary + p.writing;
}

/** Short labels for the active writing rules, for the aside chips. */
export function ruleChips(rules: Partial<WritingRules>): { key: string; label: string; required: boolean }[] {
  const out: { key: string; label: string; required: boolean }[] = [];
  const mode = (m: string | undefined) => m && m !== "off";
  if (mode(rules.capitalization)) out.push({ key: "capitalization", label: "Capital letters", required: rules.capitalization === "required" });
  if (mode(rules.punctuation)) out.push({ key: "punctuation", label: "Punctuation", required: rules.punctuation === "required" });
  if (mode(rules.completeSentences))
    out.push({ key: "completeSentences", label: "Complete sentences", required: rules.completeSentences === "required" });
  if (rules.person && mode(rules.person.mode)) {
    const p = rules.person.person;
    out.push({ key: "person", label: `${p === 1 ? "1st" : p === 2 ? "2nd" : "3rd"} person`, required: rules.person.mode === "required" });
  }
  if (rules.minWords && mode(rules.minWords.mode))
    out.push({ key: "minWords", label: `${rules.minWords.count}+ words`, required: rules.minWords.mode === "required" });
  if (mode(rules.noTextSpeak)) out.push({ key: "noTextSpeak", label: "No text-speak", required: rules.noTextSpeak === "required" });
  if (mode(rules.descriptive)) out.push({ key: "descriptive", label: "Descriptive", required: rules.descriptive === "required" });
  if (mode(rules.spelling)) out.push({ key: "spelling", label: "Spelling", required: rules.spelling === "required" });
  return out;
}

/** The empty learning view, for stories that switch learning on mid-session. */
export function emptyLearning(): LearningView {
  return { gradeLevel: null, pendingCards: [], targetWords: [], rules: {}, points: { ...ZERO_POINTS }, glossary: [] };
}

/** The sentence on the chapter-transition card, before the player presses Next. */
export function transitionText(e: {
  reason: "advance" | "fail";
  fromChapterTitle: string;
  chapter: { title: string };
  message?: string;
}): string {
  if (e.reason === "fail") {
    return `${e.message ?? "You failed."} You're going back to ${e.chapter.title}. Click Next to continue.`;
  }
  return `You completed ${e.fromChapterTitle} and are moving to ${e.chapter.title}. Click Next to move to the next chapter.`;
}
